import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  const { clear } = useCart();
  const polled = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrderId(data.order_id);
          clear();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        polled.current += 1;
        if (polled.current < 10) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div data-testid="order-success" className="max-w-3xl mx-auto px-6 py-32 text-center">
      {status === "checking" && (
        <>
          <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">◦ Verifying</div>
          <h1 className="font-serif text-5xl tracking-tight">One moment…</h1>
          <p className="mt-6 text-muted-foreground">Confirming your payment with the studio.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="text-xs tracking-[0.3em] uppercase text-primary mb-4">◦ Confirmed</div>
          <h1 className="font-serif text-5xl lg:text-6xl tracking-tight font-light">
            Thank you<span className="text-primary">.</span>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-md mx-auto">
            Your order has been received. The maker is being notified — you'll hear from them soon.
          </p>
          <div className="mt-12 flex gap-4 justify-center">
            <Link to="/account" data-testid="view-orders-link" className="btn-primary px-6 py-3 text-sm rounded-sm">View orders</Link>
            <Link to="/shop" className="border border-foreground/80 px-6 py-3 text-sm rounded-sm hover:bg-foreground hover:text-background transition-colors">Keep wandering</Link>
          </div>
          {orderId && <div className="mt-8 text-xs text-muted-foreground">Order #{orderId.slice(0, 8)}</div>}
        </>
      )}
      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <h1 className="font-serif text-4xl tracking-tight">Something interrupted the payment.</h1>
          <p className="mt-6 text-muted-foreground">Your bag is still saved — please try again.</p>
          <Link to="/cart" className="btn-primary mt-8 inline-block px-6 py-3 text-sm rounded-sm">Back to bag</Link>
        </>
      )}
    </div>
  );
}
